import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Clock, MapPin, ArrowLeft, Fingerprint, History, 
  Wallet, Loader2, CheckCircle, RefreshCw, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const UnifiedPonto = () => {
  const navigate = useNavigate();
  const { 
    isAuthenticated, hasJornyxAccess, jornyxUser, jornyxToken, 
    JORNYX_API_URL, getJornyxAuthHeaders 
  } = useUnifiedAuth();
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [marcacoesHoje, setMarcacoesHoje] = useState([]);
  const [bancoHoras, setBancoHoras] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrando, setRegistrando] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/unified/login');
      return;
    }
    if (!hasJornyxAccess) {
      toast.error('Você não tem acesso ao módulo de ponto');
      navigate('/unified');
      return;
    }

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [isAuthenticated, hasJornyxAccess, navigate]);

  const loadData = useCallback(async () => {
    if (!jornyxToken) return;
    
    setLoading(true);
    try {
      const headers = getJornyxAuthHeaders();
      
      const [marcacoesRes, bancoRes] = await Promise.all([
        fetch(`${JORNYX_API_URL}/api/app/marcacoes-hoje`, { headers }),
        fetch(`${JORNYX_API_URL}/api/app/banco-horas`, { headers })
      ]);

      if (marcacoesRes.ok) {
        const data = await marcacoesRes.json();
        setMarcacoesHoje(data.marcacoes || []);
      }

      if (bancoRes.ok) {
        const data = await bancoRes.json();
        setBancoHoras(data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  }, [jornyxToken, JORNYX_API_URL, getJornyxAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Obter localização
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          });
        },
        (err) => console.log('Geolocalização não disponível:', err.message)
      );
    }
  }, []);

  const handleRegistrarPonto = async () => {
    setRegistrando(true);
    try {
      const response = await fetch(`${JORNYX_API_URL}/api/app/registrar-ponto`, {
        method: 'POST',
        headers: getJornyxAuthHeaders(),
        body: JSON.stringify({
          latitude: location?.latitude,
          longitude: location?.longitude
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Ponto registrado com sucesso!');
        loadData();
      } else {
        toast.error(data.detail || 'Erro ao registrar ponto');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setRegistrando(false);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="px-4 py-4 border-b border-white/10">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/unified')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-semibold text-white">Controle de Ponto</h1>
              {jornyxUser && (
                <p className="text-slate-400 text-sm flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {jornyxUser.empresa_nome || 'Empresa'}
                </p>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={loadData}
              disabled={loading}
              className="text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </header>

        {/* Conteúdo */}
        <main className="flex-1 px-4 py-6">
          <div className="max-w-lg mx-auto space-y-6">
            {/* Relógio */}
            <Card className="border-0 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <CardContent className="pt-6 text-center">
                <p className="text-emerald-100 text-sm capitalize">{formatDate(currentTime)}</p>
                <p className="text-5xl font-bold my-4 font-mono">{formatTime(currentTime)}</p>
                
                {location && (
                  <p className="text-emerald-200 text-xs flex items-center justify-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Localização capturada
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Botão de registrar ponto */}
            <Button
              onClick={handleRegistrarPonto}
              disabled={registrando}
              className="w-full h-16 bg-white hover:bg-slate-100 text-emerald-600 font-semibold text-lg shadow-lg"
              data-testid="btn-registrar-ponto"
            >
              {registrando ? (
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
              ) : (
                <Fingerprint className="w-6 h-6 mr-2" />
              )}
              {registrando ? 'Registrando...' : 'Registrar Ponto'}
            </Button>

            {/* Marcações do dia */}
            <Card className="border-0 bg-white/5 backdrop-blur">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-medium text-white">Marcações de Hoje</h3>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                  </div>
                ) : marcacoesHoje.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">
                    Nenhuma marcação registrada hoje
                  </p>
                ) : (
                  <div className="space-y-2">
                    {marcacoesHoje.map((marcacao, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                          <span className="text-white font-mono">
                            {marcacao.hora || marcacao.horario}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">
                          {marcacao.tipo || `Marcação ${index + 1}`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Banco de horas */}
            {bancoHoras && (
              <Card className="border-0 bg-white/5 backdrop-blur">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Wallet className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-medium text-white">Banco de Horas</h3>
                  </div>
                  
                  <div className="text-center">
                    <p className={`text-3xl font-bold ${
                      (bancoHoras.saldo_minutos || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {bancoHoras.saldo_formatado || '00:00'}
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      Saldo atual
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default UnifiedPonto;
